<template>
  <div
    class="next-draws-filter"
    :class="{
      'next-draws-filter--static': noInteractionHighlight,
      'next-draws-filter--has-date': hasSelectedDate,
    }"
    role="group"
  >
    <div
      class="visual-facade"
      aria-hidden="true"
    >
      <span
        class="display-date"
        :class="{ 'display-date--placeholder': !hasSelectedDate }"
      >{{ hasSelectedDate ? displayDate : placeholder }}</span>
      <svg
        viewBox="0 0 24 24"
        class="calendar-icon"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zM7 12h5v5H7v-5z" />
      </svg>
    </div>
    <input
      type="date"
      class="native-date-input"
      :value="modelValue"
      :min="allowPast ? undefined : today"
      :aria-label="displayDate || 'Select date'"
      @change="handleInput"
      @input="handleInput"
    >
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults, defineOptions } from 'vue';

defineOptions({ name: 'NextDrawsFilter' });

const props = withDefaults(defineProps<{
  modelValue?: string;
  allowPast?: boolean;
  placeholder?: string;
  /** When false, clearing the native input emits '' instead of today */
  fallbackToToday?: boolean;
  /** No hover/focus border or outline (e.g. Results screen) */
  noInteractionHighlight?: boolean;
}>(), {
  modelValue: () => new Date().toISOString().split('T')[0],
  allowPast: false,
  placeholder: '',
  fallbackToToday: true,
  noInteractionHighlight: false
});

const hasSelectedDate = computed(() => Boolean(props.modelValue?.trim()));

const today = new Date().toISOString().split('T')[0];

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const displayDate = computed(() => {
  if (!props.modelValue) return '';
  const parts = props.modelValue.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
  return props.modelValue;
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const next = target.value || (props.fallbackToToday ? today : '');
  emit('update:modelValue', next);
};
</script>

<style scoped>
.next-draws-filter {
  position: relative;
  width: 100%;
  min-height: 50px;
  touch-action: manipulation;
}

.visual-facade {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 50px;
  padding: 0 16px;
  box-sizing: border-box;
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--results-filter-radius, 8px);
  transition: border-color 0.2s;
  pointer-events: none;
}

.next-draws-filter:hover .visual-facade {
  border-color: var(--colour-orange-button);
}

.next-draws-filter:focus-within .visual-facade {
  border-color: var(--colour-orange-button);
  outline: 2px solid var(--colour-orange-button);
  outline-offset: 2px;
}

.next-draws-filter--static:hover .visual-facade,
.next-draws-filter--static:focus-within .visual-facade {
  border-color: var(--colour-surface-cont);
  outline: none;
}

.next-draws-filter--static .visual-facade {
  transition: none;
}

.native-date-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0 16px;
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  cursor: pointer;
  z-index: 2;
  font-size: 16px;
  line-height: 50px;
  outline: none;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;
}

.native-date-input:hover,
.native-date-input:focus,
.native-date-input:focus-visible {
  outline: none;
  box-shadow: none;
  border: none;
}

/* WebKit / Chromium: stretch the calendar control across the full row */
.native-date-input::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
  opacity: 0;
}

.display-date {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 16px;
  font-weight: 500;
  color: var(--colour-secondary-text-colour);
  flex: 1;
  min-width: 0;
  text-align: left;
}

.next-draws-filter--has-date .display-date {
  color: var(--colour-base-text-colour);
}

.calendar-icon {
  width: 20px;
  height: 20px;
  fill: var(--colour-secondary-text-colour);
  flex-shrink: 0;
}

.next-draws-filter--has-date .calendar-icon {
  fill: var(--colour-base-text-colour);
}

@media (max-width: 768px) {
  .next-draws-filter,
  .visual-facade {
    min-height: 44px;
    height: 44px;
  }

  .native-date-input {
    line-height: 44px;
  }

  .visual-facade {
    padding: 0 12px;
  }

  .display-date {
    font-size: 14px;
  }

  .calendar-icon {
    width: 18px;
    height: 18px;
  }
}
</style>
